import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Import protective equipment images
import safetyGlasses from "../../../utils/protective-equipment-symbols/safety_glasses.svg";
import faceShield from "../../../utils/protective-equipment-symbols/face_shield.svg";
import dustMask from "../../../utils/protective-equipment-symbols/dust_mask.svg";
import gasMask from "../../../utils/protective-equipment-symbols/gas_mask.svg";
import gloves from "../../../utils/protective-equipment-symbols/gloves.svg";
import protectiveSuit from "../../../utils/protective-equipment-symbols/protective_suit.svg";
import safetyShoes from "../../../utils/protective-equipment-symbols/safety_shoes.svg";
import helmet from "../../../utils/protective-equipment-symbols/helmet.svg";
import hearingProtection from "../../../utils/protective-equipment-symbols/hearing_protection.svg";
import weldingMask from "../../../utils/protective-equipment-symbols/welding_mask.svg";
import hairProtection from "../../../utils/protective-equipment-symbols/hair_protection_cover.svg";
import warningWest from "../../../utils/protective-equipment-symbols/warning_west.svg";

const protectiveEquipment = [
  {
    id: "safety_glasses",
    name: "Sikkerhetsbriller",
    description: "Beskyttelse mot flygende partikler og kjemiske støv",
    category: "eye_protection",
    image: safetyGlasses,
  },
  {
    id: "goggles",
    name: "Kjemiske briller",
    description: "Beskyttelse mot kjemiske støv og væsker",
    category: "eye_protection",
    image: safetyGlasses,
  },
  {
    id: "face_shield",
    name: "Ansiktsvern",
    description: "Beskyttelse av hele ansiktet mot kjemiske støv og væsker",
    category: "face_protection",
    image: faceShield,
  },
  {
    id: "respirator",
    name: "Støvmasker",
    description: "Beskyttelse mot støv og partikler",
    category: "respiratory_protection",
    image: dustMask,
  },
  {
    id: "chemical_respirator",
    name: "Kjemiske masker",
    description: "Beskyttelse mot kjemiske damper og gasser",
    category: "respiratory_protection",
    image: gasMask,
  },
  {
    id: "gloves",
    name: "Hansker",
    description: "Beskyttelse av hender mot kjemiske støv og væsker",
    category: "hand_protection",
    image: gloves,
  },
  {
    id: "chemical_gloves",
    name: "Kjemiske hansker",
    description: "Beskyttelse av hender mot spesifikke kjemikalier",
    category: "hand_protection",
    image: gloves,
  },
  {
    id: "lab_coat",
    name: "Laboratoriefrakk",
    description: "Beskyttelse av kropp og klær mot kjemiske støv og væsker",
    category: "body_protection",
    image: protectiveSuit,
  },
  {
    id: "chemical_suit",
    name: "Kjemisk drakt",
    description: "Full kroppsbeskyttelse mot kjemiske støv og væsker",
    category: "body_protection",
    image: protectiveSuit,
  },
  {
    id: "safety_shoes",
    name: "Sikkerhetssko",
    description: "Beskyttelse av føtter mot kjemiske støv og væsker",
    category: "foot_protection",
    image: safetyShoes,
  },
  {
    id: "helmet",
    name: "Hjelm",
    description: "Beskyttelse av hodet mot slag og støt",
    category: "head_protection",
    image: helmet,
  },
  {
    id: "hearing_protection",
    name: "Hørselsvern",
    description: "Beskyttelse mot høye lyder og støy",
    category: "hearing_protection",
    image: hearingProtection,
  },
  {
    id: "welding_mask",
    name: "Sveisemaske",
    description: "Beskyttelse mot lys og varme ved sveising",
    category: "face_protection",
    image: weldingMask,
  },
  {
    id: "hair_protection",
    name: "Hårbeskyttelse",
    description: "Beskyttelse av hår mot kjemiske støv og væsker",
    category: "head_protection",
    image: hairProtection,
  },
  {
    id: "warning_west",
    name: "Advarselsvest",
    description: "Synlighet og identifikasjon på arbeidsplassen",
    category: "visibility_protection",
    image: warningWest,
  },
];

export const ProtectiveEquipmentSelector = ({
  value = [],
  onChange,
  label,
}) => {
  const { t } = useTranslation();
  const [selectedEquipment, setSelectedEquipment] = useState(value);

  useEffect(() => {
    setSelectedEquipment(value);
  }, [value]);

  const handleEquipmentToggle = (equipmentId) => {
    const newSelection = selectedEquipment.includes(equipmentId)
      ? selectedEquipment.filter((id) => id !== equipmentId)
      : [...selectedEquipment, equipmentId];

    setSelectedEquipment(newSelection);
    onChange?.(newSelection);
  };

  const categories = [
    { id: "eye_protection", name: "Øyebeskyttelse" },
    { id: "face_protection", name: "Ansiktsbeskyttelse" },
    { id: "respiratory_protection", name: "Respiratorisk beskyttelse" },
    { id: "hand_protection", name: "Håndbeskyttelse" },
    { id: "body_protection", name: "Kroppsbeskyttelse" },
    { id: "foot_protection", name: "Fotbeskyttelse" },
    { id: "head_protection", name: "Hodebeskyttelse" },
    { id: "hearing_protection", name: "Hørselsvern" },
    { id: "visibility_protection", name: "Synlighet" },
  ];

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {label || t("chemical.inventory.personal_protection")}
      </label>

      {categories.map((category) => (
        <div key={category.id} className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {protectiveEquipment
              .filter((equipment) => equipment.category === category.id)
              .map((equipment) => (
                <div
                  key={equipment.id}
                  className={`relative border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedEquipment.includes(equipment.id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleEquipmentToggle(equipment.id)}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedEquipment.includes(equipment.id)}
                      onChange={() => handleEquipmentToggle(equipment.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-center mb-2">
                        <img
                          src={equipment.image}
                          alt={equipment.name}
                          className="w-12 h-12 object-contain"
                        />
                      </div>
                      <p className="text-sm font-medium text-gray-900 text-center">
                        {equipment.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        {equipment.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
